<script setup>
import { ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import { formatCurrency, numberWithCommas } from '@/plugins/misc'
import {
  useUsersStore,
  useReservationsStore,
  useUserGroupsStore,
  useRestaurantsStore
} from '@/stores'
import noResEmptyState from '@/assets/images/ui/no_reservations_empty_state.jpg'
import Testimonials from "@core/components/Testimonials.vue"
import ProfileMenu from "@/layouts/components/ProfileMenu.vue"

// Route setup
const route = useRoute()
const router = useRouter()

// Store initialization
const usersStore = useUsersStore()
const userGroupsStore = useUserGroupsStore()
const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()

// Local refs - define these before store refs
const tab = ref('Bookings')
const memberToShow = ref(null)
const tabsSetInitially = ref(false)
const rmToast = ref(null)
const selectedCity = ref("")
const selectedFilter = ref("All")

// Store refs - ensure stores are initialized before destructuring
const myUser = computed(() => usersStore.myUser)
const users = computed(() => usersStore.users)
const loginDialogMode = computed({
  get: () => usersStore.loginDialogMode,
  set: value => usersStore.loginDialogMode = value
})
const isRunningInStandAlone = computed(() => usersStore.isRunningInStandAlone)
const isShowingPreferredRequestInfoDialog = computed(() => usersStore.isShowingPreferredRequestInfoDialog)

const myReservations = computed(() => reservationsStore.myReservations)
const editingReservation = computed(() => reservationsStore.editingReservation)
const fetching = computed(() => reservationsStore.fetching)

const viewingDetailsRestaurant = computed(() => restaurantsStore.viewingDetailsRestaurant)
const restaurants = computed(() => restaurantsStore.restaurants)
const viewingDetailsRestaurantTab = computed(() => restaurantsStore.viewingDetailsRestaurantTab)
const restaurantByNameAndMarket = computed(() => restaurantsStore.restaurantByNameAndMarket)

// Get last path segment for routing logic
const lastPathSegment = route.params.all

// Auto-redirect from /m/ to /m/profileId or login page for unauthenticated users
if (!lastPathSegment) {
  if (myUser.value) {
    if (myUser.value?.profileId) {
      router.push({ path: `/m/${myUser.value.profileId}`, query: route.query })
    } else {
      router.push({ path: `/m/`, query: route.query })
    }
  } else {
    // Redirect if not logged in - but only if not coming from login to prevent back button loop
    // This is a special case (can't use allowLoggedInOnly) because /m/<something> still needs to work
    const fromLogin = router.options.history.state.back?.includes('/login')
    if (!fromLogin) {
      router.push({ name: 'login', query: { to: '/m/' } })
    } else {
      // If coming from login via back button, go to home instead to prevent loop
      router.push('/home/')
    }
  }
}

// Auto-scroll deep-linking for ?reservations query parameter
if (route.query.reservations) {
  setTimeout(() => {
    const reservationElement = document.getElementById("my-reservations")
    if (reservationElement) {
      const elementPosition = reservationElement.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top: elementPosition - 50, // Subtract 50px to scroll up a bit
        behavior: "smooth"
      })
    }
  }, 500)
}

// JWT validation for authentication state
const jwt = JSON.parse(localStorage.getItem('jwt') || 'null')

// localStorage cleanup for authenticated users
if (jwt && myUser.value) {
  // Prevent redirect issues on /home, /home2 after auth
  localStorage.removeItem('guestUserGroup')
  localStorage.removeItem('homeRedirectPath')

  // Set auth timestamp for home2 form mode
  localStorage.setItem('userLastAuthenticatedAt', moment().format())
}

// Toast notification function
function showToast() {
  if (rmToast.value) {
    rmToast.value.show()
  }
}

// Declare the stop watch function outside so it's accessible in onBeforeUnmount
let stopRouteWatch = null

// Watch for filter changes and persist to localStorage
watch(selectedCity, newCity => {
  localStorage.setItem('memberBookingsSelectedCity', newCity || '')
})

watch(selectedFilter, newFilter => {
  localStorage.setItem('memberBookingsSelectedFilter', newFilter || 'All')
})

// Use last segment path to fetch user
onMounted(async () => {
  let profileUrlSegment = Array.isArray(route.params.all) ? route.params.all[0]?.toLowerCase() : route.params.all?.toLowerCase()
  if (myUser?.value?.profileId == profileUrlSegment) {
    // Fetch fresh data for own profile to ensure counts are up-to-date
    await usersStore.fetchUserById(myUser.value._id)

    // Use the fetched user from users array, not myUser
    memberToShow.value = users.value.find(u => u._id == myUser.value._id) || myUser.value
  } else if (profileUrlSegment) {
    await usersStore.getUserByProfileId(profileUrlSegment)
    memberToShow.value = users.value.find(u => u.profileId == profileUrlSegment)
  } else {
    // Fetch fresh data for own profile
    if (myUser.value?._id) {
      await usersStore.fetchUserById(myUser.value._id)
      memberToShow.value = users.value.find(u => u._id == myUser.value._id) || myUser.value
    } else {
      memberToShow.value = myUser.value
    }
  }

  // Watch reactive route parameter instead of static variable
  stopRouteWatch = watch(() => Array.isArray(route.params.all) ? route.params.all[0]?.toLowerCase() : route.params.all?.toLowerCase(), (newVal, oldVal) => {
    if (myUser?.value?.profileId == newVal && myUser?.value?._id) {
      // Fetch fresh data for own profile
      usersStore.fetchUserById(myUser.value._id).then(() => {
        memberToShow.value = users.value.find(u => u._id == myUser.value._id) || myUser.value
      })
    } else if (newVal) {
      usersStore.getUserByProfileId(newVal).then(() => {
        memberToShow.value = users.value.find(u => u.profileId == newVal)
      })
    }
  })

  // Load persisted filter selections
  const savedCity = localStorage.getItem('memberBookingsSelectedCity')
  if (savedCity) {
    selectedCity.value = savedCity
  }

  const savedFilter = localStorage.getItem('memberBookingsSelectedFilter')
  if (savedFilter) {
    selectedFilter.value = savedFilter
  }

  // Show toast for unauthenticated users
  if (!myUser.value) {
    showToast()
  }

  // RM-150: ?openEditProfile=1 → auto-open the profile editor on this page.
  // Set when an admin clicks "Edit Profile" while booking on behalf — see
  // EditReservationDialog/EditReservationReviewStep.vue#editProfileForContext.
  // Strip the query param after handling so a reload doesn't re-open the dialog.
  if (route.query.openEditProfile && myUser.value) {
    usersStore.editMyUser()
    const { openEditProfile: _omit, ...rest } = route.query
    router.replace({ path: route.path, query: rest })
  }
})

// Watch myUser and update memberToShow if it changes
watch(myUser, (newVal, oldVal) => {
  let profileUrlSegment = Array.isArray(route.params.all) ? route.params.all[0]?.toLowerCase() : route.params.all?.toLowerCase()
  if (myUser?.value?.profileId == profileUrlSegment && myUser?.value) {
    // Fetch fresh data for own profile
    if (myUser.value._id) {
      usersStore.fetchUserById(myUser.value._id).then(() => {
        memberToShow.value = users.value.find(u => u._id == myUser.value?._id) || myUser.value
      })
    } else {
      memberToShow.value = myUser.value
    }
  } else if (profileUrlSegment) {
    usersStore.getUserByProfileId(profileUrlSegment).then(() => {
      memberToShow.value = users.value.find(u => u.profileId == profileUrlSegment)
    })
  }
})

// Hook into the event when navigating to the view
onBeforeMount(() => {
  // Perform any necessary actions before entering the view
  console.log('Navigating to the view')
  if (myUser.value) reservationsStore.getMyReservations()
  reservationsStore.setupMyReservationsListeners()
})

// Hook into the event when navigating away from the view
onBeforeUnmount(() => {
  // Perform any necessary actions before leaving the view
  console.log('Navigating away from the view')
  reservationsStore.removeMyReservationsListeners()
  if (stopRouteWatch) {
    stopRouteWatch()
  }
})

// View state
const showByBookingDate = ref(true)
const showByBookingDateLast12 = ref(false)
const showByBookingDateAllTime = ref(false)
const showByRestaurantName = ref({})
const showByRestaurants = ref(false)
const showByGroup = ref(false)
const showByGroupName = ref({})
const showByPeriod = ref({ 'All Time': false, "last 12 months": false })
const showingGroups = ref({
  Active: false,
  Upcoming: false,
  Experienced: false,
  Other: false
})

// Table headers
const insightsByRestaurantHeaders = ref([
  { title: 'Restaurant/Group' },
  { title: 'Bookings' },
  { title: 'Total Spend' },
  { title: '' },
])

// City selector computed property - only shows cities where user has bookings
const selectableCities = computed(() => {
  // Get all user's reservations (active + complete + NA)
  const allUserReservations = [
    ...activeReservations.value,
    ...completeReservations.value,
    ...NAReservations.value
  ]

  // Count bookings per city
  const cityCounts = {}
  allUserReservations.forEach(reservation => {
    if (reservation.market) {
      cityCounts[reservation.market] = (cityCounts[reservation.market] || 0) + 1
    }
  })

  // Convert to array and sort by count (descending)
  const cities = Object.entries(cityCounts).map(([market, count]) => ({
    value: market,
    text: `${market} (${count} Booking${count !== 1 ? 's' : ''})`
  }))

  // Sort by booking count (highest first)
  cities.sort((a, b) => {
    const countA = parseInt(a.text.match(/\((\d+)/)?.[1] || 0)
    const countB = parseInt(b.text.match(/\((\d+)/)?.[1] || 0)
    
    return countB - countA
  })

  // Add "All Cities" option at the beginning with total count
  const totalCount = allUserReservations.length
  
  return [
    { value: '', text: `All Cities (${totalCount} Booking${totalCount !== 1 ? 's' : ''})` },
    ...cities
  ]
})

// Filter options
const filterOptions = ref([
  { title: 'All', value: 'All' },
  { title: 'Last Month', value: 'last_month' },
  { title: 'Last 3 Months', value: 'last_3_months' },
  { title: 'Last 6 Months', value: 'last_6_months' },
  { title: 'Last Year', value: 'last_year' }
])

// Computed properties
const isMyProfile = computed(() => {
  if (route.query.public !== undefined) return false
  
  return myUser?.value?._id === memberToShow?.value?._id
})

const bookingsCount = computed(() =>
  activeReservations.value.length +
  completeReservations.value.length +
  NAReservations.value.length +
  directReservations.value.length
)

const myFavoritesCount = computed(() => {
  // Use backend-calculated count for consistency
  // Backend calculates this by querying: Restaurant.find({ favoritedBy: user._id })
  return memberToShow.value?.favoritedRestaurantsCount || 0
})

// Computed properties for reservation filtering
const directReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el => el?.directBooking)
  
  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

const acceptedReservations = computed(() => {
  if (!myReservations.value) return []

  return myReservations.value.filter(el =>
    !el?.directBooking &&
    el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
    el.status === "Accepted" &&
    (el.date || el.preferredTime || el.partySize || el.preferredRestaurant)
  ).reverse()
})

const completeReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el => {
    if (el.alternateRestaurants?.length === 0 && !el.preferredRestaurant) {
      return false
    }
    
    return !el?.directBooking &&
      el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
      (el.status === "Accepted" && moment(el.sortDateTime).isBefore(moment())) &&
      !(el.status === "Draft" ||
        el.status === 'Awaiting Response from Restaurant' ||
        el.status === 'In Review' ||
        el.status === 'Member Action' ||
        el.status === 'Pending RM Review')
  })

  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

const NAReservations = computed(() => {
  if (!myReservations.value) return []

  const reservations = myReservations.value.filter(el =>
    !el?.directBooking &&
    el.userPermissions.some(e => e?.email === myUser?.value?.email) &&
    moment(el.lastStatusChangeAt).isBefore(moment().subtract(24, 'hours')) &&
    ["Canceling", "Rescinding", "Not Available", "No Response", "Declined", "Canceled", "Rescinded"].includes(el.status)
  )

  return reservations.sort((a, b) => moment(b.sortDateTime).diff(moment(a.sortDateTime)))
})

// Helper function to filter by city and date
const applyFilters = reservations => {
  let filtered = [...reservations]

  // Apply city filter
  if (selectedCity.value) {
    filtered = filtered.filter(res => res.market === selectedCity.value)
  }

  // Apply date filter
  if (selectedFilter.value && selectedFilter.value !== 'All') {
    const now = moment()
    let cutoffDate

    switch (selectedFilter.value) {
    case 'last_month':
      cutoffDate = now.clone().subtract(1, 'month')
      break
    case 'last_3_months':
      cutoffDate = now.clone().subtract(3, 'months')
      break
    case 'last_6_months':
      cutoffDate = now.clone().subtract(6, 'months')
      break
    case 'last_year':
      cutoffDate = now.clone().subtract(1, 'year')
      break
    default:
      cutoffDate = null
    }

    if (cutoffDate) {
      filtered = filtered.filter(res => moment(res.date).isAfter(cutoffDate))
    }
  }

  return filtered
}

// Filtered versions of reservation arrays
const filteredActiveReservations = computed(() => applyFilters(activeReservations.value))
const filteredCompleteReservations = computed(() => applyFilters(completeReservations.value))
const filteredNAReservations = computed(() => applyFilters(NAReservations.value))

const reservationGroupings = computed(() => {
  const groups = [
    { name: 'Active', items: filteredActiveReservations.value, emptyState: true }
  ]

  if (filteredCompleteReservations.value.length > 0) {
    groups.push({ name: 'Experienced', items: filteredCompleteReservations.value, emptyState: false })
  }

  if (filteredNAReservations.value.length > 0) {
    groups.push({ name: 'Canceled or N/A', items: filteredNAReservations.value, emptyState: false })
  }

  return groups
})

const pastAndCurrentAcceptedBookings = computed(() => {
  const rmAndDirect = [...acceptedReservations.value, ...directReservations.value]
  
  return rmAndDirect.sort((a, b) => moment(b.date).diff(moment(a.date)))
})

const pastAndCurrentAcceptedBookingsLast12Month = computed(() =>
  pastAndCurrentAcceptedBookings.value.filter(booking =>
    moment(booking.date).isAfter(moment().subtract(12, 'months'))
  )
)

const oldestBookingMoment = computed(() => {
  return pastAndCurrentAcceptedBookings.value.reduce((oldest, booking) => {
    const bookingDate = moment(booking.date)
    
    return !oldest || bookingDate.isBefore(oldest) ? bookingDate : oldest
  }, null)
})

const newestBookingMoment = computed(() => {
  return pastAndCurrentAcceptedBookings.value.reduce((newest, booking) => {
    const bookingDate = moment(booking.date)
    
    return !newest || bookingDate.isAfter(newest) ? bookingDate : newest
  }, null)
})


// TODO confirm this is the intended result
const activeReservations = computed(() => {
  var returnArray = []

  if (myReservations.value) {

    returnArray = myReservations.value.filter(function (el) {
      return !el?.directBooking
        && el.userPermissions.some(e => e?.email === myUser?.value?.email)
        && ((el.status === "Draft" || el.status == 'Awaiting Response from Restaurant' || el.status == 'In Review' || el.status == 'Member Action' || el.status == 'Pending RM Review')
          || (moment(el.lastStatusChangeAt).isSameOrAfter(moment().subtract(24, 'hours'))
            && (el.status === "Canceling" || el.status == 'Rescinding' || el.status == 'Not Available' || el.status == 'No Response' || el.status == 'Declined' || el.status == 'Canceled' || el.status == 'Rescinded'))
          || (el.userPermissions.some(e => e?.email === myUser?.value?.email) && (el.status == "Accepted" && moment(el.sortDateTime).isAfter(moment())))
        )
        && (el.date || el.preferredTime || el.partySize || el.preferredRestaurant)
    })
    returnArray = returnArray.slice().reverse()
  }

  // Sort by status priority: Accepted first, then Awaiting Response, then Not Available, then Draft
  // Within Draft status, sort by date (with date first, then without date)
  // Within each status group, sort by upcoming date (earliest first)
  returnArray.sort(function (a, b) {
    // Define status priority order
    const statusPriority = {
      'Accepted': 1,
      'Awaiting Response from Restaurant': 2,
      'Pending RM Review': 2,
      'Not Available': 3,
      'Draft': 4,
      'In Review': 5,
      'Member Action': 6
    }
    
    const priorityA = statusPriority[a.status] || 999
    const priorityB = statusPriority[b.status] || 999
    
    // First sort by status priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }
    
    // Special handling for Draft status: drafts with date, time, and party size come before drafts without all three
    if (a.status === 'Draft' && b.status === 'Draft') {
      const hasCompleteInfoA = !!(a.date && (a.confirmedTime || a.preferredTime) && a.partySize)
      const hasCompleteInfoB = !!(b.date && (b.confirmedTime || b.preferredTime) && b.partySize)
      
      // If one has complete info and other doesn't, prioritize the one with complete info
      if (hasCompleteInfoA && !hasCompleteInfoB) return -1
      if (!hasCompleteInfoA && hasCompleteInfoB) return 1
      
      // If both have complete info or both don't, sort by date (earliest first)
      if (hasCompleteInfoA && hasCompleteInfoB) {
        const dateA = moment(a.sortDateTime || a.date || a.preferredTime || '9999-12-31')
        const dateB = moment(b.sortDateTime || b.date || b.preferredTime || '9999-12-31')
        
        return dateA.diff(dateB)
      }
      
      // If neither has complete info, maintain current order
      return 0
    }
    
    // For other statuses, sort by upcoming date (earliest first)
    const dateA = moment(a.sortDateTime || a.date || a.preferredTime || '9999-12-31')
    const dateB = moment(b.sortDateTime || b.date || b.preferredTime || '9999-12-31')
    
    return dateA.diff(dateB)
  })

  return returnArray
})

// Methods
const memberCanGift = () => {
  if (!myUser.value) return false

  // Check group subscription
  const userGroup = myUser.value.userGroups?.find(
    group => group.type === 'enterprise' || group.type === 'friend'
  )
  const hasGroupSubscription = userGroup?.platinumSubscriptionsByState.current?.status === 'Active' &&
    userGroup.platinumSubscriptionsByState.current?.planId === 'enterprise-1cycle-annual'

  // Check primary account subscription
  const hasPrimarySubscription = !myUser.value.isSecondaryAccount &&
    myUser.value.platinumSubscriptionsByState.current?.status === 'Active' &&
    myUser.value.platinumSubscriptionsByState.current?.planId === 'individual-1cycle-annual'

  // Check secondary account subscription
  const hasSecondarySubscription = myUser.value.isSecondaryAccount &&
    myUser.value.platinumSubscriptionsByState.current?.status === 'Active' &&
    (myUser.value.platinumSubscriptionsByState.current?.planId === 'individual-1cycle-annual' ||
      myUser.value.platinumSubscriptionsByState.current?.planId === 'enterprise-1cycle-annual')

  // Check restaurant account
  const hasRestaurantAccess = myUser.value.primaryGroupType === 'restaurant' &&
    myUser.value.isPlatinum

  // Return true if any condition is met
  return hasGroupSubscription ||
    hasPrimarySubscription ||
    hasSecondarySubscription ||
    hasRestaurantAccess ||
    myUser.value.userLevel === 'admin'
}

const bestSpend = booking => {
  if (booking.actualSpendTotal) return booking.actualSpendTotal
  if (booking.stripeHoldMaxAmount) return booking.stripeHoldMaxAmount
  
  return Math.round(booking.minSpendPerPerson * booking.partySize * 1.32)
}

const newDraft = async (market, restaurant) => {
  try {
    await usersStore.showLoadingOverlay()
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market })
  } catch (error) {
    console.error('Error creating new draft:', error)
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const showRestaurantDetails = (marketName, restaurantName) => {
  const restaurant = restaurants.value.find(r =>
    r.name === restaurantName && r.market === marketName
  )

  if (restaurant) {
    viewingDetailsRestaurantTab.value = "Details"
    viewingDetailsRestaurant.value = restaurant
  }
}

const copyPageURL = () => {
  navigator.clipboard.writeText(window.location.href)
  console.log('Page URL copied to clipboard')
}

// Watchers
watch([activeReservations, completeReservations], ([newActive, newComplete]) => {
  if (!tabsSetInitially.value && (newActive.length > 0 || newComplete.length > 0)) {
    tabsSetInitially.value = true
    showingGroups.value.Active = newActive.length > 0
    showingGroups.value.Experienced = !newActive.length && newComplete.length > 0
  }
})
</script>

<template>
  <div>
    <!-- Main Content -->
    <div class="main-wrapper">
      <!-- Loading State -->
      <div v-if="memberToShow === null">
        <div class="loading-logo">
          <img
            src="/logo_no_border.png"
            height="100%"
            alt="Logo"
          >
        </div>
        <div class="loading">
          <div class="effect-1 effects" />
          <div class="effect-2 effects" />
          <div class="effect-3 effects" />
        </div>
      </div>

      <!-- Member Profile -->
      <div
        v-else-if="memberToShow"
        class=""
      >
        <!-- Profile Header -->
        <div class="flex justify-space-between align-center max-content-width">
          <VBtn
            v-if="myUser?.userLevel === 'admin' && isRunningInStandAlone && !isMyProfile"
            class="ml-auto"
            variant="text"
            @click="copyPageURL"
          >
            <VIcon class="mr-2">
              mdi-link
            </VIcon>
            Copy Page Url
          </VBtn>
        </div>

        <!-- Profile Content -->
        <Profile
          v-if="myUser?.userLevel !== 'dummy'"
          class="pt-2 pb-2"
          :member="memberToShow"
        />

        <!-- Profile Menu (inline when viewing own profile) -->
        <div
          v-if="myUser && isMyProfile && myUser.userLevel !== 'dummy'"
          class="max-content-width"
        >
          <ProfileMenu />
        </div>
      </div>

      <!-- Member Not Found -->
      <div v-else>
        <div
          class="text-center"
          style="padding-top: 150px"
        >
          Member Not Found
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="memberToShow !== null"
      class="d-flex flex-column align-center justify-center"
    >
      <p
        class="rm-footer-message text-center text-wrap mt-4 pa-4"
        style="white-space: normal; max-width: 460px;"
      >
        Network of Artists, Athletes, CEOs, Investors &amp; Restaurateurs who highly value where they dine
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rm-footer-message {
  font-size: 12px;
}

.main-wrapper {
  width: 100%;
  margin: 0 auto;
  padding: 0 0.4rem;
}

.tab-height {
  height: 60px !important;
}

.upcoming-empty-state {
  min-height: 36vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.reservations-loading-spinner {
  position: absolute;
  right: 5px;
  top: 20px;
}

.book-now-holder {
  text-align: center;
}

.header-new-reservation-button-sticky-holder {
  overflow: visible;
  position: sticky;
  z-index: 10;
  top: 5.5rem;
}

:deep(.v-btn--active) {
  border-bottom: 1px solid;
}

:deep(.v-field__input) {
  font-size: 16px !important;
}

:deep(.v-autocomplete .v-autocomplete__selection-text) {
  overflow: unset !important;
}

:deep(.v-table > .v-table__wrapper > table > tbody > tr > td) {
  padding: 0 !important;
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  font-weight: bold;
  padding: 0 !important;
}

:deep(.outer-table > .v-table__wrapper > table) {
  padding-left: 4px !important;
}

:deep(.inner-table > .v-table__wrapper > table) {
  padding-left: 2px !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  allowLoggedInOnly: false
</route>
